import React, { useEffect, useRef, useState, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { MapPin, Navigation, Loader2, Radio, Clock, Truck, Route, Target, Settings2, Save, AlertTriangle } from 'lucide-react';
import DashboardLayout from '../components/DashboardLayout';
import ShareEtaButton from '../components/ShareEtaButton';
import { useAuth } from '../contexts/AuthContext';
import { startTracking, stopTracking, isNativeApp, getSession, subscribe } from '../native/backgroundTracker';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const L = typeof window !== 'undefined' ? window.L : null;
const MILE_M = 1609.34;

const fmtAgo = (iso) => {
  if (!iso) return '—';
  const s = Math.max(0, Math.floor((Date.now() - new Date(iso).getTime()) / 1000));
  if (s < 60) return `${s}s ago`;
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  return `${Math.floor(s / 3600)}h ago`;
};
const fmtClock = (iso) => (iso ? new Date(iso).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' }) : '—');
const fmtDur = (min) => {
  if (min == null) return '—';
  if (min < 60) return `${min} min`;
  return `${Math.floor(min / 60)}h ${min % 60}m`;
};
const prettyStatus = (s) => (s || '').replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());

// ---------------- Driver: share my location ----------------
// UI over the single tracking session in native/backgroundTracker.js. The session is
// started/stopped ONLY by the driver's tap (or when the load is delivered) and keeps
// running while the app is minimised (native) — this component just mirrors its state.
const DriverTracker = () => {
  const [sess, setSess] = useState(getSession());
  const [busy, setBusy] = useState(false);
  const [, tick] = useState(0);

  useEffect(() => subscribe(setSess), []);
  useEffect(() => { const t = setInterval(() => tick((n) => n + 1), 15000); return () => clearInterval(t); }, []);

  const server = sess.server || {};
  const sharing = sess.active;
  const zone = server.auto_status && server.auto_status.pending ? server.auto_status : null;
  const loadStatus = server.load_status || null;

  // Load delivered → the tracker core already ended the session (works on any screen);
  // here we only surface the confirmation to the driver once.
  useEffect(() => {
    if (!sharing && sess.stopReason === 'delivered') {
      toast.success('Load delivered — location sharing stopped', { duration: 8000 });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sharing, sess.stopReason]);
  useEffect(() => {
    const a = server.auto_status;
    if (a && a.to && !a.pending) toast.success(`You arrived — load marked ${prettyStatus(a.to)} automatically`, { duration: 8000 });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [server.auto_status?.to, server.auto_status?.pending]);

  const start = async () => {
    setBusy(true);
    const res = await startTracking();
    setBusy(false);
    if (!res.ok && res.reason === 'denied') {
      toast.error('Location permission is required to share your position with dispatch.');
    }
  };
  const stop = () => { stopTracking(); toast.info('Location sharing stopped'); };

  const permissionBlocked = !sharing && (sess.permission === 'denied' || sess.permission === 'unavailable');

  return (
    <div className="max-w-lg mx-auto" data-testid="driver-tracker">
      <div className="bg-white border border-slate-200 rounded-lg p-6 text-center">
        <div className={`w-16 h-16 rounded-full mx-auto flex items-center justify-center mb-4 ${sharing ? 'bg-emerald-100' : 'bg-slate-100'}`}>
          <Radio className={`w-8 h-8 ${sharing ? 'text-emerald-600 animate-pulse' : 'text-slate-400'}`} />
        </div>
        <h2 className="text-xl font-bold text-slate-900" style={{ fontFamily: 'Work Sans, sans-serif' }}>Live Location Sharing</h2>
        <p className="text-sm text-slate-600 mt-1" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
          {isNativeApp()
            ? 'Share your position with dispatch while you drive. Tracking keeps running with your phone locked or the app in the background, and stops the moment you tap Stop.'
            : 'Share your position with dispatch while you drive. Keep this screen open — the browser must stay open to track.'}
          {' '}When you reach the shipper or receiver, your load status updates automatically. Your location is never collected outside an active session.
        </p>
        <div className="mt-4 text-sm">
          <span data-testid="tracking-status" className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full font-semibold ${sharing ? 'bg-emerald-50 text-emerald-700' : permissionBlocked ? 'bg-red-50 text-red-700' : 'bg-slate-50 text-slate-600'}`}>
            {sharing && <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" />} {sess.status}
          </span>
        </div>
        {sharing && sess.startedAt && (
          <p className="text-xs text-slate-600 mt-2" data-testid="tracking-started-at">
            Tracking since <b>{new Date(sess.startedAt).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })}</b>
            {server.load_id ? ' · linked to your active load' : ''}
          </p>
        )}
        {loadStatus && <p className="text-xs text-slate-600 mt-2" data-testid="driver-load-status">Load status: <b>{prettyStatus(loadStatus)}</b></p>}
        {zone && (
          <p className="text-xs text-[#002FA7] mt-2 font-medium" data-testid="zone-pending">
            <Target className="w-3 h-3 inline mr-1" /> You're in the {zone.kind} zone — confirming arrival ({zone.hits}/{zone.needed})…
          </p>
        )}
        {sess.lastFixAt && sharing && <p className="text-xs text-slate-400 mt-2" data-testid="tracking-last-fix">Last GPS fix: {fmtAgo(sess.lastFixAt)}{sess.lastSentAt ? ` · sent ${fmtAgo(sess.lastSentAt)}` : ''}</p>}
        {sharing && server.tracking === false && (
          <p className="text-xs text-amber-600 mt-2">You have no active load right now, so dispatch won't see a pin until one is assigned.</p>
        )}
        {permissionBlocked && (
          <p className="text-xs text-red-600 mt-2" data-testid="tracking-permission-help">
            {sess.permission === 'denied'
              ? 'Allow location for CivilX in your phone Settings (While Using the App is enough), then tap Start again.'
              : 'Turn on Location Services in your phone Settings, then tap Start again.'}
          </p>
        )}
        <button
          data-testid="toggle-sharing"
          onClick={sharing ? stop : start}
          disabled={busy}
          className={`mt-5 w-full py-3 rounded-md font-semibold text-white transition-colors disabled:opacity-60 ${sharing ? 'bg-red-600 hover:bg-red-700' : 'bg-[#002FA7] hover:bg-[#00247D]'}`}
        >
          {busy ? 'Starting…' : sharing ? 'Stop sharing' : 'Start sharing my location'}
        </button>
      </div>
    </div>
  );
};

// ---------------- Admin: geofence auto-status settings ----------------
const TrackingSettingsCard = ({ canEdit }) => {
  const [s, setS] = useState(null);
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    axios.get(`${API}/tracking/settings`, { withCredentials: true }).then((r) => setS(r.data)).catch(() => {});
  }, []);
  const save = async () => {
    setSaving(true);
    try {
      const r = await axios.put(`${API}/tracking/settings`, s, { withCredentials: true });
      setS(r.data);
      toast.success('Tracking settings saved');
    } catch (e) { toast.error(e?.response?.data?.detail || 'Could not save'); } finally { setSaving(false); }
  };
  if (!s) return null;
  return (
    <div className="bg-white border border-slate-200 rounded-lg p-3 mb-4 flex flex-wrap items-center gap-4" data-testid="tracking-settings-card">
      <div className="flex items-center gap-2 text-sm font-bold text-slate-900"><Settings2 className="w-4 h-4 text-[#002FA7]" /> Geofence auto-status</div>
      <label className="flex items-center gap-2 text-sm text-slate-700">
        <input type="checkbox" data-testid="auto-status-toggle" disabled={!canEdit} checked={!!s.auto_status_enabled}
               onChange={(e) => setS({ ...s, auto_status_enabled: e.target.checked })} className="h-4 w-4 accent-[#002FA7]" />
        Auto-flip Assigned → At Shipper and In Transit → Delivered on zone entry
      </label>
      <label className="flex items-center gap-2 text-sm text-slate-700">Radius
        <input type="number" step="0.1" min="0.05" max="5" data-testid="geofence-radius-input" disabled={!canEdit}
               value={s.geofence_radius_miles} onChange={(e) => setS({ ...s, geofence_radius_miles: Number(e.target.value) })}
               className="w-20 border border-slate-300 rounded px-2 py-1 text-sm" /> mi
      </label>
      <label className="flex items-center gap-2 text-sm text-slate-700">Confirm pings
        <input type="number" min="1" max="5" data-testid="confirm-pings-input" disabled={!canEdit}
               value={s.confirm_pings} onChange={(e) => setS({ ...s, confirm_pings: Number(e.target.value) })}
               className="w-16 border border-slate-300 rounded px-2 py-1 text-sm" />
      </label>
      {canEdit && (
        <button data-testid="tracking-settings-save" onClick={save} disabled={saving}
                className="ml-auto inline-flex items-center gap-1 px-3 py-1.5 rounded-md bg-[#002FA7] hover:bg-[#00247D] text-white text-sm font-semibold disabled:opacity-50">
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Save
        </button>
      )}
    </div>
  );
};

// ---------------- Admin / Dispatch: live map ----------------
const DispatchMap = () => {
  const mapRef = useRef(null);
  const mapObj = useRef(null);
  const layers = useRef({});
  const [drivers, setDrivers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);

  useEffect(() => {
    if (!L || !mapRef.current || mapObj.current) return;
    mapObj.current = L.map(mapRef.current).setView([39.5, -98.35], 4); // US center
    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      attribution: '&copy; OpenStreetMap', maxZoom: 19,
    }).addTo(mapObj.current);
  }, []);

  const fetchActive = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/tracking/active`, { withCredentials: true });
      setDrivers(r.data?.drivers || []);
    } catch { /* keep last */ } finally { setLoading(false); }
  }, []);

  useEffect(() => {
    fetchActive();
    const id = setInterval(fetchActive, 20000);
    return () => clearInterval(id);
  }, [fetchActive]);

  // Render markers/trails/routes when data changes
  useEffect(() => {
    const map = mapObj.current;
    if (!L || !map) return;
    const seen = new Set();
    const bounds = [];
    drivers.forEach((d) => {
      seen.add(d.driver_id);
      let g = layers.current[d.driver_id];
      if (!g) { g = { marker: null, trail: null, stop: null, route: null, fence: null }; layers.current[d.driver_id] = g; }
      const icon = L.divIcon({
        className: '', html: `<div style="background:${d.is_late ? '#dc2626' : '#002FA7'};color:#fff;border:2px solid #fff;border-radius:50%;width:26px;height:26px;display:flex;align-items:center;justify-content:center;font-size:11px;font-weight:700;box-shadow:0 1px 4px rgba(0,0,0,.4)">🚚</div>`,
        iconSize: [26, 26], iconAnchor: [13, 13],
      });
      const ll = [d.lat, d.lng];
      bounds.push(ll);
      if (g.marker) { g.marker.setLatLng(ll); g.marker.setIcon(icon); } else g.marker = L.marker(ll, { icon }).addTo(map);
      const etaLine = d.distance_miles != null
        ? `<br/>${d.distance_miles} mi ${d.eta_source === 'road' ? 'by road' : '(straight line)'} · ${fmtDur(d.eta_minutes)} · ETA ${fmtClock(d.eta_at)}` : '';
      g.marker.bindPopup(`<b>${d.driver_name || 'Driver'}</b><br/>Load ${d.load_label || ''}<br/>${prettyStatus(d.load_status)}<br/>${d.next_stop?.label ? 'Next: ' + d.next_stop.label : ''}${etaLine}`);
      // breadcrumb trail
      if (d.trail && d.trail.length > 1) {
        if (g.trail) g.trail.setLatLngs(d.trail); else g.trail = L.polyline(d.trail, { color: '#002FA7', weight: 3, opacity: 0.5 }).addTo(map);
      }
      // road route to next stop
      if (d.route_geometry && d.route_geometry.length > 1) {
        if (g.route) g.route.setLatLngs(d.route_geometry);
        else g.route = L.polyline(d.route_geometry, { color: '#16a34a', weight: 4, opacity: 0.7, dashArray: '8 8' }).addTo(map);
      } else if (g.route) { map.removeLayer(g.route); g.route = null; }
      // next stop marker + geofence ring
      if (d.next_stop?.coord) {
        const sc = [d.next_stop.coord[0], d.next_stop.coord[1]];
        bounds.push(sc);
        if (g.stop) g.stop.setLatLng(sc);
        else g.stop = L.circleMarker(sc, { radius: 8, color: '#16a34a', fillColor: '#16a34a', fillOpacity: 0.8 }).addTo(map);
        g.stop.bindPopup(`Next stop: ${d.next_stop.label}`);
        const rad = (d.next_stop.geofence_radius_miles || 0.3) * MILE_M;
        if (g.fence) { g.fence.setLatLng(sc); g.fence.setRadius(rad); }
        else g.fence = L.circle(sc, { radius: rad, color: '#16a34a', weight: 1, fillColor: '#16a34a', fillOpacity: 0.12 }).addTo(map);
      }
    });
    // remove stale
    Object.keys(layers.current).forEach((id) => {
      if (!seen.has(id)) {
        const g = layers.current[id];
        ['marker', 'trail', 'stop', 'route', 'fence'].forEach((k) => g[k] && map.removeLayer(g[k]));
        delete layers.current[id];
      }
    });
    if (bounds.length && !selected) { try { map.fitBounds(bounds, { padding: [50, 50], maxZoom: 12 }); } catch {} }
  }, [drivers, selected]);

  const focus = (d) => {
    setSelected(d.driver_id);
    if (mapObj.current) mapObj.current.setView([d.lat, d.lng], 13);
    layers.current[d.driver_id]?.marker?.openPopup();
  };

  const lateDrivers = drivers.filter((d) => d.is_late);

  return (
    <div className="space-y-4">
    {lateDrivers.length > 0 && (
      <div className="bg-red-50 border border-red-200 rounded-lg p-3 flex flex-wrap items-center gap-2 text-sm text-red-800" data-testid="late-arrivals-banner">
        <AlertTriangle className="w-4 h-4 shrink-0" />
        <span className="font-semibold">{lateDrivers.length} load{lateDrivers.length !== 1 ? 's' : ''} running late:</span>
        {lateDrivers.map((d) => (
          <button key={d.driver_id} onClick={() => focus(d)} className="underline decoration-dotted" data-testid={`late-banner-${d.driver_id}`}>
            {d.load_label} · {d.driver_name} ({fmtDur(d.late_minutes)} past {fmtClock(d.appointment_at)} {d.next_stop?.kind})
          </button>
        ))}
      </div>
    )}
    <div className="grid grid-cols-1 lg:grid-cols-4 gap-4" data-testid="dispatch-map">
      <div className="lg:col-span-3 rounded-lg overflow-hidden border border-slate-200">
        <div ref={mapRef} style={{ height: '70vh', width: '100%' }} data-testid="tracking-map" />
      </div>
      <div className="lg:col-span-1">
        <div className="bg-white border border-slate-200 rounded-lg p-3 h-full">
          <h3 className="text-sm font-bold text-slate-900 mb-2 flex items-center gap-2"><Navigation className="w-4 h-4 text-[#002FA7]" /> Active drivers ({drivers.length})</h3>
          {loading ? (
            <div className="flex items-center gap-2 text-sm text-slate-500 p-2"><Loader2 className="w-4 h-4 animate-spin" /> Loading…</div>
          ) : drivers.length === 0 ? (
            <p className="text-sm text-slate-500 p-2" data-testid="no-active-drivers">No drivers are sharing location right now. Drivers share automatically from the Live Tracking screen while on an active load.</p>
          ) : (
            <ul className="space-y-2 max-h-[62vh] overflow-y-auto">
              {drivers.map((d) => (
                <li key={d.driver_id} data-testid={`driver-item-${d.driver_id}`} onClick={() => focus(d)}
                    className="p-2 rounded-md border border-slate-100 hover:bg-slate-50 cursor-pointer">
                  <div className="flex items-center gap-2 font-semibold text-sm text-slate-900"><Truck className="w-3.5 h-3.5 text-[#002FA7]" /> {d.driver_name}</div>
                  <div className="text-xs text-slate-500 mt-0.5">Load {d.load_label} · {prettyStatus(d.load_status)}</div>
                  {d.next_stop && <div className="text-xs text-slate-600 mt-0.5 flex items-center gap-1"><MapPin className="w-3 h-3" /> {d.next_stop.label}</div>}
                  {d.distance_miles != null && (
                    <div className="text-xs text-emerald-700 mt-0.5 flex items-center gap-1 flex-wrap" data-testid={`driver-eta-${d.driver_id}`}>
                      <Clock className="w-3 h-3" /> {d.distance_miles} mi · {fmtDur(d.eta_minutes)} · ETA {fmtClock(d.eta_at)}
                      <span className={`ml-1 px-1.5 py-0.5 rounded text-[10px] font-semibold ${d.eta_source === 'road' ? 'bg-emerald-50 text-emerald-700' : 'bg-slate-100 text-slate-500'}`} data-testid={`eta-source-${d.driver_id}`}>
                        <Route className="w-2.5 h-2.5 inline mr-0.5" />{d.eta_source === 'road' ? 'road' : 'straight-line'}
                      </span>
                    </div>
                  )}
                  {d.appointment_at && d.distance_miles != null && (
                    <div className={`text-xs mt-0.5 flex items-center gap-1 ${d.is_late ? 'text-red-700 font-semibold' : 'text-slate-500'}`} data-testid={`late-status-${d.driver_id}`}>
                      {d.is_late ? <AlertTriangle className="w-3 h-3" /> : <Clock className="w-3 h-3" />}
                      {d.is_late ? `Running ${fmtDur(d.late_minutes)} late` : (d.late_minutes > 0 ? `${d.late_minutes} min behind` : 'On time')} · appt {fmtClock(d.appointment_at)}
                    </div>
                  )}
                  {d.in_zone && <div className="text-xs text-[#002FA7] mt-0.5 flex items-center gap-1 font-medium" data-testid={`in-zone-${d.driver_id}`}><Target className="w-3 h-3" /> In {d.next_stop?.kind} zone — confirming</div>}
                  {d.last_auto_status && <div className="text-[11px] text-slate-500 mt-0.5">Auto: {prettyStatus(d.last_auto_status.from)} → {prettyStatus(d.last_auto_status.to)} {fmtAgo(d.last_auto_status.at)}</div>}
                  <div className="flex items-center justify-between mt-1">
                    <div className="text-[11px] text-slate-400">Updated {fmtAgo(d.updated_at)}</div>
                    {d.load_id && <span onClick={(e) => e.stopPropagation()}><ShareEtaButton loadId={d.load_id} shareToken={d.share_token} compact /></span>}
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
    </div>
  );
};

const LiveTracking = () => {
  const { user } = useAuth();
  const isDriver = user?.role === 'driver';
  const canEdit = user?.role === 'company_admin' || user?.role === 'super_admin';
  return (
    <DashboardLayout>
      <div className="p-4 md:p-8 max-w-7xl mx-auto" data-testid="live-tracking-page">
        <div className="mb-6">
          <h1 className="text-3xl sm:text-4xl font-bold tracking-tight text-slate-900" style={{ fontFamily: 'Work Sans, sans-serif' }}>Live Tracking</h1>
          <p className="text-sm text-slate-600" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
            {isDriver ? 'Share your GPS with dispatch while you run your load.' : "Follow your drivers' realtime travels toward pickup and delivery — road-routed ETAs, geofence zones, and automatic status updates."}
          </p>
        </div>
        {!L && <div className="p-3 mb-4 bg-amber-50 border border-amber-200 rounded text-sm text-amber-800">Map library still loading — refresh if the map doesn't appear.</div>}
        {!isDriver && <TrackingSettingsCard canEdit={canEdit} />}
        {isDriver ? <DriverTracker /> : <DispatchMap />}
      </div>
    </DashboardLayout>
  );
};

export default LiveTracking;
